// components/LearningGoalCard.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  CircularProgress
} from '@mui/material';
import { PlayArrow as PlayIcon } from '@mui/icons-material';
import BookIcon from '@mui/icons-material/MenuBook';
import generalApi from '../../../generalApi';
import languageLearningApi from '../middleware/languageLearningApi';

const LearningGoalCard = ({ userId, courseId, cardStyle, onClick }) => {
  const [loading, setLoading] = useState(true);
  const [goalData, setGoalData] = useState(null);
  const [error, setError] = useState(null);

  console.log('LearningGoalCard received props:', { userId, courseId });

  useEffect(() => {
    const fetchGoalData = async () => {
      if (!userId) {
        console.warn('Missing userId for LearningGoalCard');
        setLoading(false);
        return;
      }
      
      if (!courseId) {
        console.warn('Missing courseId for LearningGoalCard');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log(`Fetching data from: /progress/user/${userId}/course/${courseId}`);
        let response, todayEntry, today;
        try {
          response = await generalApi.get(`/progress/user/${userId}/course/${courseId}`);
          console.log('Goal progress response:', response.data);

          today = new Date();
          const todayString = today.toISOString().split('T')[0];
          console.log('Looking for entries with date:', todayString);
          
          // Filter for today's entry
          todayEntry = response.data.data.find(entry => {
            const entryDate = new Date(entry.date).toISOString().split('T')[0];
            return entryDate === todayString;
          });
        
          console.log('Today\'s entry:', todayEntry);
        } catch(err){
          console.log('Error ', err);
          todayEntry = '';
        }
        
        // Get today's date in YYYY-MM-DD format
        
        
        // If no entry for today, fetch the goal and create a new progress entry
        if (!todayEntry) {
          console.log('No entry for today, fetching goal...');
          try {
            // Step 1: Fetch the user's goal settings for this course using the new API endpoint
            console.log('!!!! userId courseId ', userId, ' ', courseId);
            const goalResponse = await languageLearningApi.get(`/user-goals/${userId}/${courseId}`);
            console.log('Goal response:', goalResponse.data);
            
            if (goalResponse.data.success && goalResponse.data.goal) {
              // Step 2: Extract the sentenceCountForGoal from the response
              const { sentenceCountForGoal } = goalResponse.data.goal;
              
              // Step 3: Create a new progress entry for today with the goal count
              console.log('Creating new progress entry with sentencesRemaining:', sentenceCountForGoal);
              const createResponse = await languageLearningApi.post(`/user-goals/${userId}/${courseId}`, {
                sentencesRemaining: sentenceCountForGoal,
                date: today
              });
              
              console.log('Create response:', createResponse.data);
              
              if (createResponse.data.success) {
                setGoalData(createResponse.data.data);
              } else {
                setError('Failed to create goal progress');
              }
            } else {
              setError('Failed to fetch goal or goal not found');
            }
          } catch (goalError) {
            console.error('Error handling goal data:', goalError);
            setError('Unable to set up today\'s goal');
          }
        } else {
          // Set today's entry as the goal data
          setGoalData(todayEntry);
        }
      } catch (error) {
        console.error('Error fetching goal data:', error);
        setError('Unable to load goal data');
      } finally {
        setLoading(false);
      }
    };

    fetchGoalData();
  }, [userId, courseId]);

  if (loading) {
    return (
      <Card sx={cardStyle}>
        <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress size={24} />
          <Typography variant="body2" sx={{ mt: 2 }}>
            Loading goal data...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={cardStyle}>
        <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Typography variant="body2" color="error">{error}</Typography>
        </CardContent>
      </Card>
    );
  }

  // If no entry for today, show default view (this should rarely happen now since we create one)
  if (!goalData) {
    return (
      <Card sx={cardStyle} onClick={onClick}>
        <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Avatar sx={{ bgcolor: theme => theme.palette.secondary.main, mr: 2 }}>
              <PlayIcon fontSize="small" />
            </Avatar>
            <Typography variant="h6" fontWeight="600">
              Start Learning
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2
          }}>
            <Typography variant="body2" color="text.secondary">
              Continue learning new sentences & vocab.
            </Typography>

            <Box sx={{
              width: 60,
              height: 60,
              borderRadius: '50%',
              bgcolor: theme => theme.palette.secondary.light,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: theme => `3px solid ${theme.palette.secondary.main}`,
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
              ml: 2,
              position: 'relative'
            }}>
              <BookIcon
                sx={{
                  color: 'white',
                  fontSize: 32,
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '1em',
                  height: '1em'
                }}
              />
            </Box>
          </Box>

          <Button
            variant="contained"
            color="secondary"
            size="large"
            startIcon={<PlayIcon />}
            sx={{
              fontWeight: 600,
              py: 1.5,
              width: '100%',
              mt: 'auto'
            }}
          >
            Continue Learning
          </Button>
        </CardContent>
      </Card>
    );
  }

  const hasRemainingGoal = goalData.sentencesRemaining > 0;

  return (
    <Card sx={cardStyle} onClick={onClick}>
      <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: theme => theme.palette.secondary.main, mr: 2 }}>
            <PlayIcon fontSize="small" />
          </Avatar>
          <Typography variant="h6" fontWeight="600">
            Start Learning
          </Typography>
        </Box>

        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2
        }}>
          <Typography variant="body2" color="text.secondary">
            {hasRemainingGoal 
              ? `${goalData.sentencesRemaining} sentences remaining to complete today's goal`
              : 'You\'ve completed your daily goal! 🎉'}
            {!hasRemainingGoal && 
              <Box component="span" sx={{ display: 'block', mt: 1 }}>
                Feel free to exceed your goal if you like
              </Box>
            }
          </Typography>

          <Box sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            bgcolor: theme => theme.palette.secondary.light,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: theme => `3px solid ${theme.palette.secondary.main}`,
            boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            ml: 2,
            position: 'relative'
          }}>
            {hasRemainingGoal ? (
              <Typography variant="h5" fontWeight="600" color="white">
                {goalData.sentencesRemaining}
              </Typography>
            ) : (
              <BookIcon
                sx={{
                  color: 'white',
                  fontSize: 32,
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  width: '1em',
                  height: '1em'
                }}
              />
            )}
          </Box>
        </Box>

        <Button
          variant="contained"
          color="secondary"
          size="large"
          startIcon={<PlayIcon />}
          sx={{
            fontWeight: 600,
            py: 1.5,
            width: '100%',
            mt: 'auto'
          }}
        >
          {hasRemainingGoal ? 'Continue Learning' : 'Learn More'}
        </Button>
      </CardContent>
    </Card>
  );
};

export default LearningGoalCard;